import Component from '@glimmer/component'
import { AlertType, InlineAlert } from 'district-ui-client/components/inline-alert'
import type StandardsSet from 'district-ui-client/models/standards-set'
import { t } from 'ember-intl'
import type { BaseReportMetaStandardsSet } from 'district-ui-client/services/reporting'
import type SessionService from 'district-ui-client/services/session'
import { service } from '@ember/service'
import { isAfter, sub } from 'date-fns'

interface Signature {
  Args: {
    report?: Nullable<{
      meta: BaseReportMetaStandardsSet
    }>
  }
}

export class StandardsSetUpdatedAlert extends Component<Signature> {
  @service session!: SessionService

  get district() {
    return this.session.currentDistrict
  }

  /* Only show when the current standards does not match the standards that the report was generated with.
   * Explicitly exclude undefined from showing the alert, this may be the case during loading of a report.
   */
  get showAlert() {
    return this.args.report && standardsSetRecentlyUpdated(this.district, this.args.report)
  }

  <template>
    {{#if this.showAlert}}
      <InlineAlert data-test-report-standards-updated-alert @type={{AlertType.Warning}}>
        <div class="space-y-2">
          <h2 class="m-0 text-sm font-medium">{{t "stateAcademicStandards.updatedAlert.title"}}</h2>
          <p class="m-0 text-sm">
            <span>{{t
                "stateAcademicStandards.updatedAlert.currentValue"
                htmlSafe=true
                name=this.district.standardsSet.name
              }}</span>
            <span>{{t "stateAcademicStandards.updatedAlert.leadTimeWarning" htmlSafe=true}}</span><br />
            <span>{{t "stateAcademicStandards.updatedAlert.oldDataWarning"}}</span>
            <span></span>
          </p>
        </div>
      </InlineAlert>
    {{/if}}
  </template>
}

function standardsSetRecentlyUpdated(
  { standardsSet, updatedAt }: { standardsSet?: StandardsSet; updatedAt?: Date },
  report: {
    meta: BaseReportMetaStandardsSet
  },
) {
  return standardsSetUpdated({ standardsSet }, report) && isWithinPast24Hours(updatedAt)
}

function standardsSetUpdated(
  { standardsSet }: { standardsSet?: StandardsSet },
  report: {
    meta: BaseReportMetaStandardsSet
  },
) {
  if (!standardsSet) return false

  return !reportHasMatchingStandardsSet(report, standardsSet)
}

function reportHasMatchingStandardsSet(report: { meta: BaseReportMetaStandardsSet }, standardsSet: StandardsSet) {
  return Boolean(report.meta.standards_set_id) && report.meta.standards_set_id === standardsSet.id
}

function isWithinPast24Hours(date?: Date) {
  if (!date) {
    return true // If no date is provided from BE default to existing behaviour
  }
  const now = new Date()
  const twentyFourHoursAgo = sub(now, { hours: 24 })
  return isAfter(date, twentyFourHoursAgo)
}
